import type { SelectOption } from '@/components/Select';
import type { OrderAppOrderReturnReason } from '@/graphql/orderApp/api';
import { getReturnReasonSubmitLabel } from '@/lib/withdrawal/returnReasonSubmitLabels';
import { paymentMessages } from '@/messages/payment';
import type { LineReturnFields } from '@/views/OrderReturnView/orderReturnTypes';

export const DEFAULT_OTHER_REASON_CODE: OrderAppOrderReturnReason = 'OTHERS';

const payloadOtherLabel = () =>
  String(paymentMessages.withdrawalOtherReasonLabel.defaultMessage ?? 'Other');

const payloadNoReasonComment = () =>
  String(
    paymentMessages.withdrawalNoReasonComment.defaultMessage ??
      'No reason given by client',
  );

export const DEFAULT_NO_REASON_COMMENT_EN = payloadNoReasonComment();
export const DEFAULT_OTHER_REASON_LABEL_EN = payloadOtherLabel();

function translatedReasonForWithdrawalSubmit(
  reasonCode: string,
  reasonOptions: SelectOption[],
): string {
  return (
    reasonOptions.find((o) => o.value === reasonCode)?.label ??
    getReturnReasonSubmitLabel(reasonCode) ??
    reasonCode
  );
}

function otherReasonLabel(reasonOptions: SelectOption[]): string {
  return (
    reasonOptions.find((o) => o.value === DEFAULT_OTHER_REASON_CODE)?.label ??
    payloadOtherLabel()
  );
}

export type WithdrawalSubmitItemInput = {
  comment?: string;
  orderLineId: string;
  quantity: number;
  reasonCode: OrderAppOrderReturnReason;
  translatedReason: string;
};

function quantityForLine(
  lineId: string,
  quantityByLineId: Record<string, number>,
): number {
  const raw = quantityByLineId[lineId];
  return typeof raw === 'number' && raw > 0 ? raw : 1;
}

export function buildWithdrawalSubmitItems(
  selectedLineIds: string[],
  lineReturnFields: Record<string, LineReturnFields>,
  reasonOptions: SelectOption[],
  quantityByLineId: Record<string, number> = {},
): WithdrawalSubmitItemInput[] {
  return selectedLineIds.map((lineId) => {
    const quantity = quantityForLine(lineId, quantityByLineId);
    const fields = lineReturnFields[lineId] ?? {
      description: '',
      reason: '',
    };
    const trimmedComment = fields.description.trim();
    const selectedReasonId = fields.reason;

    if (!selectedReasonId) {
      return {
        orderLineId: lineId,
        quantity,
        reasonCode: DEFAULT_OTHER_REASON_CODE,
        translatedReason: otherReasonLabel(reasonOptions),
        comment: trimmedComment || payloadNoReasonComment(),
      };
    }

    return {
      orderLineId: lineId,
      quantity,
      reasonCode: selectedReasonId as OrderAppOrderReturnReason,
      translatedReason: translatedReasonForWithdrawalSubmit(
        selectedReasonId,
        reasonOptions,
      ),
      comment: trimmedComment || undefined,
    };
  });
}
